import React, { useEffect, useMemo, useState } from 'react'
import { getAdminSettings } from 'enrollment-platform/utils/adminSettings'
import { Empty, message } from 'antd'
import isNil from 'lodash/isNil'
import type { Dayjs } from 'dayjs'
import {
  FileExcelOutlined,
  FilterFilled,
  InfoCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Table } from '@surecompanies/core_components'
import type { ColumnsType } from 'antd/es/table'
import { useDispatch, useSelector } from 'react-redux'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { ROLE_PLATFORM_HIXME_ADMIN } from '@hixme/role-policy'
import {
  actions as userSessionActions,
  selectors as userSessionSelectors,
} from 'store/modules/user-session'
import Page from 'layouts/Page'
import PageWrapper from 'enrollment-platform/PageWrapper'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import dayjs from 'helpers/datetime'
import { downloadFile } from 'helpers/download-file'
import {
  useGetInvoiceList,
  downloadInvoiceExcel,
} from '../../../finance/api/invoice-list-api-client'
import { EmailTemplateTag } from '../../../finance/components/InvoiceManagement/emailTemplates/EmailTemplateTag'
import {
  flattenPeriodGroups,
  groupByPeriod,
  toGroupKey,
  type TableRow,
} from './groupInvoicePeriods'
import { InvoicePeriodCell } from './InvoicePeriodCell'
import { useCanDownloadFromFundingInvoice } from './useCanDownloadFromFundingInvoice'
import {
  DownloadLink,
  FilterBar,
  FilterByLabel,
  PageContent,
  PresetButton,
  PresetGroup,
  PageSubtitle,
  TableCard,
  TableFooter,
} from './InvoiceList.styles'
import styles from './InvoiceList.module.scss'

type MonthRange = Dayjs

const ALL_TIME_START = dayjs('2000-01-01').startOf('month')

const lastMonths = (months: number): MonthRange =>
  dayjs()
    .subtract(months - 1, 'month')
    .startOf('month')

const thisYearRange = (): MonthRange => dayjs().startOf('year')

const RANGE_PRESETS: Array<{ label: string; value: () => MonthRange }> = [
  { label: 'This year', value: thisYearRange },
  { label: 'Last 6 months', value: () => lastMonths(6) },
  { label: 'Last 12 months', value: () => lastMonths(12) },
  { label: 'Last 24 months', value: () => lastMonths(24) },
  { label: 'All time', value: () => ALL_TIME_START },
]

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
})

const formatAmount = (amount: number | null): string =>
  isNil(amount) || !Number.isFinite(amount)
    ? '—'
    : currencyFormatter.format(amount)

export default function InvoiceList() {
  const [range, setRange] = useState<MonthRange>(thisYearRange)
  const [activePresetIndex, setActivePresetIndex] = useState(0)
  const [expandedGroups, setExpandedGroups] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  const [downloadingExcel, setDownloadingExcel] = useState<
    Record<string, boolean>
  >({})
  const canDownloadExcel = useCanDownloadFromFundingInvoice()

  const dispatch = useDispatch()
  const platformRole = useSelector(authSelectors.getRole)
  const isSuperAdmin = platformRole === ROLE_PLATFORM_HIXME_ADMIN

  const selectedCompanyId = useSelector(
    userSessionSelectors.getCurrentCompanyId
  )
  const urlClientId = useMemo(
    () =>
      new URLSearchParams(window.location.search).get('clientId') ?? undefined,
    []
  )

  useEffect(() => {
    if (urlClientId) {
      dispatch(userSessionActions.setCurrentCompany(urlClientId))
    }
  }, [urlClientId, dispatch])

  // clientId is required by the backend for every role (it verifies the caller
  // administers this client). Use the selected company for all roles — it is
  // set by default to the user's first/only administered client — so the query
  // fires and the preset/date filters work. A super-admin can switch companies;
  // a single-client admin simply keeps their default.
  const clientId = selectedCompanyId ?? undefined

  const { showTestData } = getAdminSettings().finance

  // Omit endMonth/endYear: invoices are sent ahead of the billing month, and a missing end is open.
  const { data, isLoading } = useGetInvoiceList(
    {
      startMonth: range.month() + 1,
      startYear: range.year(),
      clientId,
      excludeTestClients: isSuperAdmin && !showTestData ? true : undefined,
    },
    { enabled: !isNil(clientId) }
  )

  const records = data?.data ?? []
  const periodGroups = useMemo(() => groupByPeriod(records), [records])
  const tableRows = useMemo(
    () => flattenPeriodGroups(periodGroups, expandedGroups),
    [periodGroups, expandedGroups]
  )

  const handleTogglePeriod = (groupKey: string) => {
    setExpandedGroups((previous) => {
      const next = new Set(previous)
      if (next.has(groupKey)) {
        next.delete(groupKey)
      } else {
        next.add(groupKey)
      }
      return next
    })
  }

  const handleDownloadExcel = async (recordId: string, fileName: string) => {
    setDownloadingExcel((prev) => ({ ...prev, [recordId]: true }))
    try {
      const { blob, filename } = await downloadInvoiceExcel(recordId, fileName)
      downloadFile(
        blob,
        filename,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      )
      message.success('Excel downloaded successfully')
    } catch {
      message.error('Failed to download Excel. Please try again.')
    } finally {
      setDownloadingExcel((prev) => ({ ...prev, [recordId]: false }))
    }
  }

  const columns: ColumnsType<TableRow> = [
    {
      title: 'Period',
      key: 'period',
      width: '20%',
      render: (_, record) => (
        <InvoicePeriodCell
          record={record}
          isExpanded={expandedGroups.has(toGroupKey(record))}
          onTogglePeriod={handleTogglePeriod}
        />
      ),
    },
    {
      title: 'Invoice type',
      key: 'invoiceTemplate',
      width: '16%',
      render: (_, record) => (
        <EmailTemplateTag template={record.invoiceTemplate} isCustomerFacing />
      ),
    },
    {
      title: 'Issue date',
      key: 'issueDate',
      width: '16%',
      render: (_, record) => record.issueDate,
    },
    {
      title: 'Due date',
      key: 'dueDate',
      width: '16%',
      render: (_, record) => (isNil(record.dueDate) ? '—' : record.dueDate),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: '16%',
      render: (_, record) => (
        <span className={styles.amountCell}>{formatAmount(record.amount)}</span>
      ),
    },
    // The whole column goes for users who may not download invoices, rather
    // than leaving it empty.
    ...(canDownloadExcel
      ? [
          {
            title: 'Download',
            key: 'actions',
            width: '16%',
            render: (_: unknown, record: TableRow) => (
              <DownloadLink
                onClick={() => handleDownloadExcel(record.id, record.fileName)}
                disabled={downloadingExcel[record.id]}
              >
                {downloadingExcel[record.id] ? (
                  <LoadingOutlined spin />
                ) : (
                  <FileExcelOutlined />
                )}
                Excel
              </DownloadLink>
            ),
          },
        ]
      : []),
  ]

  return (
    <PageWrapper>
      <Page
        title="Invoices"
        breadcrumb={<BreadcrumbNav items={[{ title: 'Invoice List' }]} />}
      >
        <PageContent>
          <PageSubtitle>
            Monthly billing records and payment history
          </PageSubtitle>

          <FilterBar>
            <FilterByLabel>
              <FilterFilled className={styles.filterIcon} />
              Filter
            </FilterByLabel>
            <PresetGroup>
              {RANGE_PRESETS.map((preset, i) => (
                <PresetButton
                  key={preset.label}
                  $active={activePresetIndex === i}
                  onClick={() => {
                    setActivePresetIndex(i)
                    setRange(preset.value())
                  }}
                >
                  {preset.label}
                </PresetButton>
              ))}
            </PresetGroup>
            <div className={styles.countText}>
              Showing{' '}
              <span className={styles.highlight}>{periodGroups.length}</span>{' '}
              {periodGroups.length === 1 ? 'invoice' : 'invoices'}
              {!isNil(clientId) && isSuperAdmin
                ? ' for the selected company'
                : null}
            </div>
          </FilterBar>

          <TableCard>
            <Table
              dataSource={tableRows}
              columns={columns}
              rowKey="id"
              loading={isLoading}
              pagination={false}
              rowClassName={(record) => {
                if (record.rowKind === 'version') {
                  return styles.olderTableRow
                }
                return record.olderVersions.length > 0 &&
                  expandedGroups.has(toGroupKey(record))
                  ? styles.expandedPeriodRow
                  : ''
              }}
              locale={
                isNil(clientId)
                  ? {
                      emptyText: (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description={
                            isSuperAdmin
                              ? 'Select a company to view its invoices'
                              : 'No company available to view invoices'
                          }
                        />
                      ),
                    }
                  : undefined
              }
            />
            <TableFooter>
              <InfoCircleOutlined />
              <span>
                <strong>Note:</strong> Excel files contain detailed
                employee-level data including PHI. All downloads require
                justification and are logged for compliance.
              </span>
            </TableFooter>
          </TableCard>
        </PageContent>
      </Page>
    </PageWrapper>
  )
}
